import { useEffect, useState } from "react";
import { Camera, CheckCircle2, XCircle, ChevronDown, Images } from "lucide-react";
import api from "@/lib/api";

const StagePill = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full ${ok ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-400"}`}>
    {ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />} {label}
  </span>
);

export function InspectionsPanel() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/admin/inspections").then((r) => setData(r.data)).catch(() => setData({ bookings: [], angles: [] }));
  }, []);

  if (!data) return <p className="text-slate-400 text-sm">Loading…</p>;
  const labels = Object.fromEntries((data.angles || []).map((a) => [a.key, a.label]));

  const PhotoGrid = ({ inspection, legacy }) => {
    if (inspection?.photos) {
      return (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2">
          {Object.entries(inspection.photos).map(([k, p]) => (
            <button key={k} onClick={() => setLightbox({ url: p.url, label: labels[k] || k, at: p.at })} className="text-left">
              <img src={p.url} alt={labels[k] || k} className="w-full aspect-[4/3] object-cover rounded-lg border border-slate-200 hover:ring-2 hover:ring-blue-400" />
              <p className="text-[10px] text-slate-500 mt-0.5 truncate">{labels[k] || k}</p>
              <p className="text-[9px] text-slate-400">{(p.at || "").slice(0, 16).replace("T", " ")}</p>
            </button>
          ))}
        </div>
      );
    }
    if (legacy?.length) {
      return (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2">
          {legacy.map((u, i) => (
            <button key={i} onClick={() => setLightbox({ url: u, label: `Photo ${i + 1}` })}>
              <img src={u} alt={`Photo ${i + 1}`} className="w-full aspect-[4/3] object-cover rounded-lg border border-slate-200 hover:ring-2 hover:ring-blue-400" />
            </button>
          ))}
        </div>
      );
    }
    return <p className="text-slate-400 text-xs mt-2">No photos submitted.</p>;
  };

  return (
    <div data-testid="inspections-panel" className="space-y-4">
      <div className="bg-white rounded-2xl shadow-card border border-slate-100 p-5 flex items-center gap-3">
        <span className="grid place-items-center w-10 h-10 rounded-xl bg-blue-50 text-blue-600"><Camera size={19} /></span>
        <div>
          <p className="font-heading font-bold text-slate-900">Trip inspections</p>
          <p className="text-slate-400 text-xs">Timestamped check-in / check-out photos for every paid trip — use side-by-side comparison to resolve damage disputes.</p>
        </div>
      </div>

      {data.bookings.length === 0 && <p className="text-slate-400 text-sm bg-white rounded-2xl shadow-card p-8 text-center">No paid trips yet.</p>}

      {data.bookings.map((b) => {
        const isOpen = open === b.id;
        return (
          <div key={b.id} data-testid={`inspection-booking-${b.id}`} className="bg-white rounded-2xl shadow-card border border-slate-100">
            <button onClick={() => setOpen(isOpen ? null : b.id)} className="w-full flex items-center justify-between gap-3 p-5 text-left">
              <div>
                <p className="font-semibold text-slate-800">{b.car_name} <span className="text-slate-400 font-normal">· {b.customer_name}</span></p>
                <p className="text-slate-400 text-xs mt-0.5">{b.pickup_date} → {b.return_date} · {b.customer_email}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StagePill ok={b.checkin_complete || !!b.trip_photos?.customer_start?.length} label="Check-in" />
                <StagePill ok={b.checkout_complete || !!b.trip_photos?.customer_return?.length} label="Check-out" />
                <ChevronDown size={16} className={`text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </div>
            </button>
            {isOpen && (
              <div className="px-5 pb-5 grid md:grid-cols-2 gap-6 border-t border-slate-100 pt-4">
                <div>
                  <p className="label-caps text-slate-500 text-[11px] flex items-center gap-1.5"><Images size={13} /> Check-in (pickup)</p>
                  <PhotoGrid inspection={b.trip_inspections?.customer_start} legacy={b.trip_photos?.customer_start} />
                </div>
                <div>
                  <p className="label-caps text-slate-500 text-[11px] flex items-center gap-1.5"><Images size={13} /> Check-out (return)</p>
                  <PhotoGrid inspection={b.trip_inspections?.customer_return} legacy={b.trip_photos?.customer_return} />
                </div>
                {(b.trip_photos?.host_pre?.length || b.trip_photos?.host_return?.length) ? (
                  <div className="md:col-span-2">
                    <p className="label-caps text-slate-500 text-[11px]">Host photos</p>
                    <PhotoGrid legacy={[...(b.trip_photos?.host_pre || []), ...(b.trip_photos?.host_return || [])]} />
                  </div>
                ) : null}
              </div>
            )}
          </div>
        );
      })}

      {lightbox && (
        <div className="fixed inset-0 z-[70] bg-slate-900/80 grid place-items-center p-6" onClick={() => setLightbox(null)} data-testid="inspection-lightbox">
          <div className="max-w-3xl w-full">
            <img src={lightbox.url} alt={lightbox.label} className="w-full max-h-[75vh] object-contain rounded-xl" />
            <p className="text-white text-sm text-center mt-3 font-semibold">{lightbox.label}{lightbox.at ? ` · ${lightbox.at.slice(0, 19).replace("T", " ")} UTC` : ""}</p>
          </div>
        </div>
      )}
    </div>
  );
}
